import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import imageUpload from "../../../services/upload";
import {
  FormControl,
  TextField,
  Select,
  MenuItem,
  FormHelperText,
} from "@mui/material";
import { IMAGE_PATH, instanceToken } from "../../../utils/constant";
import emergencieService from "../../../services/emergencieService";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { CreateNewEmergencySchema } from "../../../schema/CreateNewEmergencySchema";
import {
  EditPageLayout,
  EditImageSection,
  EditFieldLabel,
  EditFormTopSection,
} from "../../../components/edit/EditPageLayout";

const imgFileTypes = ["image/gif", "image/jpeg", "image/png", "image/svg+xml"];

export default function CreateEmergencies({ homeAlert }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState("");
  const [imageFileUrl, setImageFileUrl] = useState("");
  const [values, setValues] = useState({ image_url: "" });
  const [category, setCategory] = useState("");
  const [errorMessage, setErrorMessage] = useState({});
  const [, setEmergencies] = useState(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(CreateNewEmergencySchema),
    initialValues: {
      name_en: "",
      name_mm: "",
      address_en: "",
      address_mm: "",
      phone: "",
      phone_note: "",
      category: "",
      link: "",
    },
  });

  async function getImageUrl(imgData) {
    const response = await imageUpload.getImageUrl(
      instanceToken.token,
      imgData
    );
    const result = response.data;
    const path = IMAGE_PATH + result?.imageName;
    setImageFileUrl(result?.imageUploadUrl);
    setValues({
      ...values,
      image_url: path,
    });
  }

  const changeCategory = (e) => {
    setCategory(e.target.value);
    setValue("category", e.target.value);
  };

  const changeFile = async (e) => {
    if (e.target.files && e.target.files[0]) {
      const img = e.target.files[0];
      if (!imgFileTypes.includes(img.type)) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Please select image. (PNG, JPG, JPEG, GIF, ...)",
        });
        return;
      }
      if (img.size > 204800) {
        setErrorMessage({
          ...errorMessage,
          image_url: "Image file size must be smaller than 200KB.",
        });
        return;
      }
      setImageFile(img);
      setImagePreview(URL.createObjectURL(img));
      getImageUrl(img);
    }
  };

  const formData = new FormData();

  const handleCreate = async (data) => {
    formData.append("name_en", data?.name_en);
    formData.append("name_mm", data?.name_mm);
    formData.append("address_en", data?.address_en);
    formData.append("address_mm", data?.address_mm);
    formData.append("phone", data?.phone);
    formData.append("phone_note", data?.phone_note);
    formData.append("link", data?.link);
    formData.append("category", data?.category);
    formData.append("image_data", "");
    formData.append("image_url", values?.image_url);

    try {
      setLoading(true);
      await imageUpload.uploadImage(imageFileUrl, imageFile);
      const response = await emergencieService.postEmergencies(
        instanceToken.token,
        formData
      );
      navigate("/emergencies");
      setEmergencies(response.data);
      homeAlert("Emergencies have been created.", false);
    } catch (error) {
      console.log(error);
      homeAlert("Error on server.", true);
    } finally {
      setLoading(false);
      setErrorMessage({});
    }
  };

  return (
    <EditPageLayout
      title="Create Emergency"
      subtitle="Add emergency contact details and information."
      breadcrumbs={[
        { label: "Emergencies", path: "/emergencies" },
        { label: "Create" },
      ]}
      listPath="/emergencies"
      loading={loading}
      submitLabel="Create"
      onSubmit={handleSubmit(handleCreate)}
    >
      <EditFormTopSection
        image={
          <EditImageSection
            uploadLabel="Emergency Image"
            previewLabel="Image Preview"
            imagePreview={imagePreview}
            onFileChange={changeFile}
            error={errorMessage["image_url"]}
            recommendedSize="340 x 180 px (1.8:1)"
          />
        }
      >
        <div>
          <EditFieldLabel required>Name (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_en"
            {...register("name_en")}
            error={!!errors.name_en}
            helperText={errors.name_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Name (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="name_mm"
            {...register("name_mm")}
            error={!!errors.name_mm}
            helperText={errors.name_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Address (English)</EditFieldLabel>
          <TextField
            fullWidth
            id="address_en"
            {...register("address_en")}
            error={!!errors.address_en}
            helperText={errors.address_en?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Address (Myanmar)</EditFieldLabel>
          <TextField
            fullWidth
            id="address_mm"
            {...register("address_mm")}
            error={!!errors.address_mm}
            helperText={errors.address_mm?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Phone</EditFieldLabel>
          <TextField
            fullWidth
            id="phone"
            {...register("phone")}
            error={!!errors.phone}
            helperText={errors.phone?.message}
          />
        </div>
        <div>
          <EditFieldLabel>Phone Note</EditFieldLabel>
          <TextField
            fullWidth
            id="phone_note"
            {...register("phone_note")}
            error={!!errors.phone_note}
            helperText={errors.phone_note?.message}
          />
        </div>
        <div>
          <EditFieldLabel required>Category</EditFieldLabel>
          <FormControl fullWidth error={!!errors.category}>
            <Select value={category} onChange={changeCategory} displayEmpty>
              <MenuItem value="" disabled>
                Select category
              </MenuItem>
              <MenuItem value="Emergency Hotline">Emergency Hotline</MenuItem>
              <MenuItem value="Ambulance">Ambulance</MenuItem>
              <MenuItem value="Oxygen">Oxygen</MenuItem>
              <MenuItem value="Blood Donation">Blood Donation</MenuItem>
            </Select>
            <FormHelperText error>
              {errors.category?.message || errorMessage["category"]}
            </FormHelperText>
          </FormControl>
        </div>
        <div>
          <EditFieldLabel>Link</EditFieldLabel>
          <TextField
            fullWidth
            id="link"
            {...register("link")}
            error={!!errors.link}
            helperText={errors.link?.message}
          />
        </div>
      </EditFormTopSection>
    </EditPageLayout>
  );
}
